import { differenceInDays, format } from 'date-fns';
import { Link } from 'react-router';
import { Center, Column, DataRow, Tooltip, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { AmountRow, EdtBadge } from '~/commonComponents';
import { isPartialShift } from '~/commonComponents/CommonFunctions';
import { PROMO_ROW_LABEL } from '~/constants/promo';
import { UserSubProfession } from '~/enums/UserProfession';
import { useAppSelector, useUserSelector } from '~/hooks';
import { SolidInformationCircleIcon } from '@tempmee/react-icons';
import { IJob, IMultipleCounters } from '~/models';
import { Colors } from '~/themes/colors';
import { getProfessionalPayChargeDate } from '~/utils/chargeTimeline';
import { currencyFormat } from '~/utils/currencyFormat';
import { EducationUtils } from '~/utils/EducationUtils';
import { PartialShift } from '../../CandidateList/components/PartialShift';
import { renderOfferExpirationDateTime } from '../../counterJobs/RenderFunc';
import { isEmployeeOptionShift } from '../../WorkerClassification/utils';
import { HighlightedLines } from './HighlightedLines';
import { useMemo } from 'react';

const ConvenienceFeeTooltip =
  'GoTu charges a 3.5% convenience fee on Credit Card transactions. If you do not wish to be charged the convenience fee, please update your billing to E-check/ACH.';

const OvertimeTooltip =
  'Overtime pay (1.5x rate) is required for hours that exceed the state’s daily and/or weekly regular working hour limits.';

interface IShiftDetailsProps {
  currentCounter: IMultipleCounters;
  hasBorderStyle?: boolean;
  isLiteVersion?: boolean;
  job?: Partial<IJob> | null;
}

export const ShiftDetails = ({
  currentCounter,
  hasBorderStyle = true,
  isLiteVersion = false,
  job: queryJob,
}: IShiftDetailsProps) => {
  const currentUser = useUserSelector();
  const { job: reduxJob } = useAppSelector((state) => state.job);
  const job = queryJob ?? reduxJob;

  const {
    isOnsiteInterview,
    job_datetime: jobDateTime,
    local_date: localDate,
    local_end_time: localEndTime,
    local_start_time: localStartTime,
    offer_owner: offerOwner,
    rate,
    specialty,
  } = job || {};
  const { payment } = currentCounter || {};

  const chargeDate = jobDateTime ? format(getProfessionalPayChargeDate(jobDateTime), 'MM/dd/yyyy') : '';
  const startDiff = jobDateTime ? differenceInDays(new Date(jobDateTime), new Date()) : 0;

  const totalAmount = payment?.totalAmount;
  const tempMeeFee = payment?.tempmeeFee;
  const tempMeeFeePercent = payment?.tempmeeFeeRatio
    ? (payment?.tempmeeFeeRatio * 100).toFixed(1)
    : 0;
  const convenienceFee = currentCounter?.payment?.convenienceFee ?? 0;
  const convenienceFeePercent = currentCounter?.payment?.convenienceFeeRatio
    ? (currentCounter?.payment?.convenienceFeeRatio * 100).toFixed(1)
    : 0;

  const hasConvenienceFee = convenienceFeePercent !== 0 && convenienceFee;
  const hasOvertimeHours = job?.regularAndOvertimeHours?.overtime > 0;
  const hasRegularHours = job?.regularAndOvertimeHours?.regular > 0;
  const hasTotalOvertime = job?.totalOvertime > 0;
  const hasTotalRegular = job?.totalRegular > 0;
  const isCounterHours = currentCounter ? isPartialShift(currentCounter) : null;
  const isCounterRate = currentCounter?.candidateRate !== rate;
  const struckHours = Boolean(isCounterHours);

  const fmtProfessionName = useMemo(() => {
    if (job?.subProfession === UserSubProfession.EFDA) {
      return 'Expanded Functions DA';
    }

    return job?.professionName;
  }, [job?.professionName, job?.subProfession]);

  return (
    <Column
      style={hasBorderStyle ? { border: `1px solid ${colors.neutral[150]}` } : undefined}
    >
      {!isLiteVersion && (
        <Column
          align="center"
          py={4}
          style={{
            backgroundColor: isOnsiteInterview
              ? Colors.jobs_interview_background
              : Colors.jobs_shift_background,
          }}
        >
          <Typography as="span" bold color={colors.primary[500]} size="heading_4" textAlign="center">
            {isOnsiteInterview ? 'Interview Details' : 'Shift Details'}
          </Typography>
        </Column>
      )}
      <Column {...(hasBorderStyle ? { px: 8, pt: 5, pb: 8 } : {})}>
        {!isLiteVersion && (
          <>
            {isCounterHours && (
              <Center data-cy="counter-hours-indicator">
                <PartialShift />
              </Center>
            )}
            {renderOfferExpirationDateTime(currentCounter?.expiration, currentUser?.time_zone)}
          </>
        )}

        <Column gap={2}>
          <DataRow divider={false}
            label="Office:"
            value={
              <Typography as="span" numberOfLines={1} style={{ maxWidth: 200 }}>
                {offerOwner?.office_name}
              </Typography>
            }
          />
          {isOnsiteInterview && (
            <DataRow divider={false}
              label="Job Title:"
              value={
                isLiteVersion ? (
                  <Typography
                    as="span"
                    color={colors.neutral[500]}
                    numberOfLines={1}
                    style={{ maxWidth: 200 }}
                  >
                    {job?.postingTitle}
                  </Typography>
                ) : (
                  <Link to={`/permanent-jobs/job-overview/${job?.postingId}`}>
                    <Typography as="span" color={colors.neutral[500]} style={{ textDecoration: 'underline' }}>
                      {job?.postingTitle}
                    </Typography>
                  </Link>
                )
              }
            />
          )}
          <DataRow divider={false} label="Date:" value={localDate} />
          <DataRow divider={false} label="Professional Type:" value={fmtProfessionName} />
          <DataRow divider={false}
            label="Preferred Specialty:"
            value={
              <Typography as="span" style={{ textTransform: 'capitalize' }}>
                {EducationUtils.removeSpecialtyPrefix(specialty) || 'General'}
              </Typography>
            }
          />
          <HighlightedLines highlight={isCounterHours}>
            <DataRow divider={false}
              data-cy="detail-row-original-hours"
              icon={<EdtBadge>{job?.local_timezone}</EdtBadge>}
              label="Hours:"
              value={
                <Typography as="span" data-cy="original-hours-value">
                  {struckHours ? (
                    <s>{`${localStartTime} - ${localEndTime}`}</s>
                  ) : (
                    `${localStartTime} - ${localEndTime}`
                  )}
                </Typography>
              }
            />
            {isCounterHours && (
              <DataRow divider={false}
                data-cy="detail-row-hours"
                label={<Typography as="span" bold>*Counter Offer (hours):</Typography>}
                value={
                  <Typography as="span" bold data-cy="hours-value" style={{ textTransform: 'lowercase' }}>
                    {`${currentCounter?.local_begin} - ${currentCounter?.local_end}`}
                  </Typography>
                }
              />
            )}
          </HighlightedLines>
          <HighlightedLines highlight={isCounterRate}>
            <AmountRow
              amount={rate}
              label="Hourly Rate:"
              struck={job?.rate !== currentCounter?.candidateRate}
              suffix="/hr"
              valueDataCy="original-rate"
            />
            {isCounterRate && (
              <DataRow divider={false}
                label={<Typography as="span" bold>*Counter Offer (rate):</Typography>}
                value={
                  <Typography as="span" bold data-cy="counter-rate">
                    ${currencyFormat(currentCounter?.candidateRate)} /hr
                  </Typography>
                }
              />
            )}
          </HighlightedLines>
          <DataRow divider={false}
            label="Meal Break:"
            value={`${job?.lunch_final_break_time} min ${job?.is_lunch_break_paid ? ' (Paid)' : ' (Unpaid)'}`}
          />
          <HighlightedLines highlight={hasOvertimeHours}>
            {hasRegularHours && hasTotalRegular && (
              <AmountRow
                amount={job?.totalRegular}
                data-cy="detail-row-regular-pay"
                label={
                  <>
                    Regular Rate Pay (
                    <span data-cy="working-hours-value">
                      {job?.regularAndOvertimeHours?.regular?.toFixed(2)}
                    </span>{' '}
                    hrs):
                  </>
                }
                valueDataCy="regular-pay-value"
              />
            )}
            {hasOvertimeHours && hasTotalOvertime && (
              <AmountRow
                amount={job?.totalOvertime}
                icon={
                  <Tooltip title={OvertimeTooltip} placement="bottom">
                    <SolidInformationCircleIcon color={colors.neutral[300]} />
                  </Tooltip>
                }
                label={`*Overtime Pay (${job?.regularAndOvertimeHours?.overtime?.toFixed(2)} hrs):`}
              />
            )}
          </HighlightedLines>
          <AmountRow
            amount={tempMeeFee}
            data-cy="detail-row-service-fee"
            label={`Service Fee${tempMeeFeePercent !== 0 ? ` (${tempMeeFeePercent}%)` : ''}:`}
            valueDataCy="service-fee"
          />
          {payment?.peoFee !== 0 && isEmployeeOptionShift(job) && (
            <AmountRow amount={payment?.peoFee} label="Employee Fee:" />
          )}
          {hasConvenienceFee && (
            <AmountRow
              amount={convenienceFee}
              data-cy="detail-row-convenience-fee"
              icon={
                <Tooltip title={ConvenienceFeeTooltip} placement="bottom">
                  <SolidInformationCircleIcon color={colors.neutral[300]} />
                </Tooltip>
              }
              label={`Convenience Fee (${convenienceFeePercent || '0'}%):`}
              valueDataCy="convenience-fee"
            />
          )}
          {job?.multi_day_booking && startDiff > 28 && !offerOwner?.is_pre_approved && (
            <DataRow divider={false} label="Professional Pay Charge Date:" value={chargeDate} />
          )}
          {job?.counterCover && (
            <AmountRow
              amount={job?.counterCover?.amount}
              data-cy="detail-row-counter-cover"
              label={
                <Typography as="span" data-cy="counter-cover-label">
                  Gotu Counter Cover:
                </Typography>
              }
              valueDataCy="counter-cover-value"
              variant="credit"
            />
          )}
          {payment?.promoDiscount?.amount ? (
            <AmountRow
              amount={payment.promoDiscount.amount}
              data-cy="detail-row-promo"
              label={
                <Typography as="span" data-cy="promo-label">
                  {`${PROMO_ROW_LABEL}:`}
                </Typography>
              }
              valueDataCy="promo-value"
              variant="credit"
            />
          ) : null}
          <AmountRow amount={totalAmount} label="Total Pay:" valueDataCy="total-pay" variant="total" />
        </Column>
      </Column>
    </Column>
  );
};
